import React, { useRef, useState } from "react";
import { api, apiError, mediaUrl } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

export const REPORT_CATEGORIES = [
  { key: "Copyright / DMCA", desc: "Someone is using your copyrighted music, images, or content." },
  { key: "Abuse & Harassment", desc: "Bullying, harassment, or threats." },
  { key: "Impersonation or Identity Theft", desc: "A profile pretending to be you, someone else, or a business." },
  { key: "Hate Speech", desc: "Content that attacks protected groups based on race, religion, etc." },
  { key: "Inappropriate Content", desc: "Nudity, sexual exploitation, or illegal acts." },
  { key: "Violence & Harm", desc: "Self-injury, graphic violence, or dangerous organizations." },
  { key: "Scams & Spam", desc: "Phishing, fraudulent transactions, or malicious links." },
  { key: "Technical Bug", desc: "Something on Spotter isn't working correctly." },
];

// Shared urgency ranking used by the admin reports panel + public form.
export const REPORT_PRIORITY = {
  "Copyright / DMCA": 1, "Abuse & Harassment": 1,
  "Impersonation or Identity Theft": 2, "Inappropriate Content": 2, "Hate Speech": 2, "Violence & Harm": 2,
  "Scams & Spam": 3, "Technical Bug": 3,
};
export const reportRank = (category) => REPORT_PRIORITY[category] || 4;
export const isUrgentReport = (category) => reportRank(category) === 1;

const TITLES = { user: "Report user", post: "Report post", message: "Report message", group: "Report Hot Spot" };

export function ReportDialog({ open, onOpenChange, targetType, targetId, targetName }) {
  const [cat, setCat] = useState(null);
  const [details, setDetails] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const reset = () => { setCat(null); setDetails(""); setImage(null); };

  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!cat) { toast.error("Pick a reason for your report."); return; }
    setSubmitting(true);
    try {
      await api.post("/report", { target_type: targetType, target_id: targetId, category: cat, details, image_url: image });
      toast.success("Report sent to the Spotter team. Thank you.");
      reset(); onOpenChange(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="border-4 border-black bg-spotter-cream max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon flex items-center gap-2"><Flag className="w-5 h-5" />{TITLES[targetType] || "Report"}{targetName ? ` — ${targetName}` : ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-spotter-maroon">You can report this for violating Spotter's Community Standards. Pick the reason that best fits:</p>
        <div className="space-y-2">
          {REPORT_CATEGORIES.map((c) => (
            <button key={c.key} data-testid={`report-cat-${c.key}`} onClick={() => setCat(c.key)}
              className={`w-full text-left p-3 rounded-xl border-2 ${cat === c.key ? "border-spotter-maroon bg-spotter-gold/30" : "border-black bg-white"} hover:bg-spotter-gold/20`}>
              <div className="font-heading font-bold text-sm">{c.key}</div>
              <div className="text-xs text-spotter-maroon">{c.desc}</div>
            </button>
          ))}
        </div>
        <Textarea data-testid="report-details" value={details} onChange={(e) => setDetails(e.target.value)}
          placeholder="Add any details to help our team (optional)…" className="border-2 border-black" />
        <div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} />
          {image ? (
            <div className="relative inline-block">
              <img src={mediaUrl(image)} alt="evidence" className="h-24 rounded-lg border-2 border-black" data-testid="report-image-preview" />
              <button data-testid="report-image-remove" onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <Button type="button" variant="outline" data-testid="report-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-2 border-black gap-2 bg-white">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}Attach a screenshot (optional)
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="report-submit" onClick={submit} disabled={submitting || !cat}
            className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}Submit report</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
